import React from 'react';
import { PRODUCT_ADD_ON_IDENTIFIER } from '@thd-olt-functional/utils';
import { formatDate, formatWeek } from './util';

import {
  FULFILLMENT_METHOD_APPLIANCE,
  FULFILLMENT_METHOD_BODFS,
  FULFILLMENT_METHOD_BOPIS,
  FULFILLMENT_METHOD_BOSS,
  FULFILLMENT_METHOD_STH, MA,
  STORE_ASSEMBLY_VALUES
} from './constants';

const {
  HDPP,
  ATTACH_LABOR,
  PRO_ASSEMBLY,
  ATTACH_LABOR_STORE_ASSEMBLY,
  PRO_REFERRAL,
  DUAL_PATH,
  SAM_DUAL_PATH,
  VIRTUAL_ASSISTANCE
} = PRODUCT_ADD_ON_IDENTIFIER;

export const getNumberItemsInCart = () => {
  return cookieUtils.getTHDNumberItemsInCart();
};

export const isLoggedIn = () => {
  return !!(typeof window !== 'undefined' && window?.THDCustomer?.default?.isLoggedIn);
};

export const isPaypalEligible = () => {
  // Initializing cookie manager
  if (typeof cookieUtils === 'undefined') {
    return false;
  }
  cookieUtils.initializeCookieManager();
  let isEligible = true;
  if (getNumberItemsInCart() > 0) {
    isEligible = false;
  } else if (isLoggedIn()) {
    isEligible = false;
  }
  return isEligible;
};

export const shouldDisplaySubbuybox = ({ fulfillment, fulfillmentMethod }) => {
  if (fulfillmentMethod === FULFILLMENT_METHOD_BODFS) {
    return false;
  }
  return !!((fulfillment?.fulfillmentOptions || []).length);
};

export const convertFulfillmentConst = ({ cartFulfillmentConst, apiFulfillmentConst }) => {
  if (cartFulfillmentConst) {
    switch (cartFulfillmentConst) {
    case FULFILLMENT_METHOD_STH:
      return 'sth';
    case FULFILLMENT_METHOD_BOSS:
      return 'boss';
    case FULFILLMENT_METHOD_BODFS:
      return 'express delivery';
    default:
      return cartFulfillmentConst.toLowerCase();
    }
  } else if (apiFulfillmentConst) {
    switch (apiFulfillmentConst) {
    case 'sth':
      return FULFILLMENT_METHOD_STH;
    case 'boss':
      return FULFILLMENT_METHOD_BOSS;
    case 'bopis':
      return FULFILLMENT_METHOD_BOPIS;
    case 'express delivery':
      return FULFILLMENT_METHOD_BODFS;
    default:
      return apiFulfillmentConst;
    }
  }
  return null;
};

export const checkInventory = (fulfillmentOptions, apiFulfillmentConst) => {
  if (apiFulfillmentConst === 'express delivery') {
    return fulfillmentOptions.some((fulfillmentOption) => {
      return (fulfillmentOption?.services || []).some((service) => service.type === apiFulfillmentConst);
    });
  }
  return fulfillmentOptions.some((fulfillmentOption) => {
    return (fulfillmentOption?.services || []).some((service) => {
      return service?.type === apiFulfillmentConst
        && (service?.locations || []).some((location) => location?.inventory?.quantity > 0);
    });
  });
};

const getBogoFulfillmentMethod = ({ fulfillmentOptions, fulfillmentMethod }) => {
  const apiFulfillmentMethod = convertFulfillmentConst({ cartFulfillmentConst: fulfillmentMethod });
  const selectedFulfillmentHasInventory = checkInventory(fulfillmentOptions, apiFulfillmentMethod);

  if (selectedFulfillmentHasInventory) return fulfillmentMethod;

  const fulfillmentsPriority = ['sth', 'bopis', 'boss', 'express delivery'];
  const firstAvailableFulfillment = fulfillmentsPriority.find((fulfillmentType) => {
    return checkInventory(fulfillmentOptions, fulfillmentType);
  });

  return firstAvailableFulfillment
    ? convertFulfillmentConst({ apiFulfillmentConst: firstAvailableFulfillment })
    : null;
};

export const getBogoCartReqParams = (bogoSelectedProduct, {
  fulfillmentMethod,
  storeZip,
  zipCode,
  storeId,
  primaryStoreNum
}) => {
  const cartReqParamsOptions = {
    itemId: bogoSelectedProduct.itemId,
    quantity: 1,
    zipCode,
    storeId,
  };

  if (bogoSelectedProduct?.identifiers?.productType === MA) {
    cartReqParamsOptions.fulfillmentMethod = FULFILLMENT_METHOD_APPLIANCE;
    cartReqParamsOptions.primaryStoreNum = primaryStoreNum;
  } else if (bogoSelectedProduct?.fulfillment?.fulfillmentOptions?.length > 0) {
    const bogoFulfillment = getBogoFulfillmentMethod({
      fulfillmentMethod,
      fulfillmentOptions: bogoSelectedProduct.fulfillment.fulfillmentOptions
    });

    if (bogoFulfillment) {
      cartReqParamsOptions.storeZip = storeZip;
      cartReqParamsOptions.fulfillmentMethod = bogoFulfillment;
      cartReqParamsOptions.fulfillment = bogoSelectedProduct.fulfillment;
    } else {
      return null;
    }
  } else {
    return null;
  }

  // eslint-disable-next-line no-use-before-define
  return getCartReqParams(cartReqParamsOptions);
};

export const getBogoCartOptions = (bogoInfo, { fulfillmentMethod, storeZip, zipCode, storeId }) => {
  if (!bogoInfo) return null;

  const { bogoSelectedProduct, isFreeProduct } = bogoInfo;
  // The "bogo" key in cartOptions tells cart overlay to render a message for a free item
  // but there are some BOGOs where you get a dollar/percent off the second item instead
  const bogo = (isFreeProduct)
    ? { itemDetails: getBogoCartReqParams(bogoSelectedProduct, { fulfillmentMethod, storeZip, zipCode, storeId }) }
    : null;
  const component = bogoSelectedProduct ? 'add both' : null;
  const sharedSection = bogoSelectedProduct ? 'bogo' : null;

  return {
    bogo,
    component,
    sharedSection
  };
};

export const getCartOptions = ({
  channel,
  configurableItems,
  fulfillmentMethod = '',
  alterBrowserHistory,
  zipCode,
  storeId,
  storeZip,
  bogoInfo
}) => {
  const misship = configurableItems ? false : [
    FULFILLMENT_METHOD_BOPIS,
    FULFILLMENT_METHOD_BOSS
  ].indexOf(fulfillmentMethod) > -1;
  const BODFS = fulfillmentMethod === FULFILLMENT_METHOD_BODFS;
  const directCheckout = !BODFS && fulfillmentMethod !== FULFILLMENT_METHOD_APPLIANCE;

  let host = 'https://www.homedepot.com';
  if (typeof window !== 'undefined') {
    const origin = window.location.origin;
    host = origin.match(/local/g) ? 'https://www.homedepot.com' : origin;
  }
  return {
    host,
    channel,
    misship,
    directCheckout,
    alterBrowserHistory,
    bypassBss: misship,
    bss: misship,
    hasConfigurableItems: !!configurableItems,
    ...(getBogoCartOptions(bogoInfo, { fulfillmentMethod, storeZip, zipCode, storeId }) || {}),
  };
};

const getNearByStore = (fulfillment) => {
  const locations = fulfillment?.locations || [];
  if (locations.length > 1) {
    const nearbyStore = locations.find((location) => !location.isAnchor);
    return nearbyStore;
  }
  return null;
};

const getLocalStore = (fulfillment) => {
  const locations = fulfillment?.locations || [];
  const localStore = locations.find((location) => location.isAnchor);
  return localStore;
};

export const getCartReqParams = ({
  itemId,
  configurableItems,
  storeZip,
  fulfillmentMethod,
  quantity,
  productAddOns,
  zipCode,
  storeId,
  fulfillment,
  isAssembleItemAtStore,
  bogoInfo,
  primaryStoreNum
}) => {
  const { warrantyItem = {}, attachedLabor = [], service = {}, subscriptions = {} } = productAddOns || {};
  // let itemIds = configurableItems?.map((item) => (item.itemId));
  let cartReqParams = configurableItems ? configurableItems.map((item) => ({
    itemId: item.itemId,
    quantity,
    fulfillmentMethod,
    isAnchor: item.isAnchor
  }))
    : {
      itemId,
      quantity,
      fulfillmentMethod
    };

  // Protection Plan (HDPP) Add on
  if (warrantyItem?.itemId) {
    cartReqParams = {
      ...cartReqParams,
      warrantyItem
    };
  }

  // Attach Labor Add on
  if (attachedLabor.length && attachedLabor[0].attachedLaborSku) {
    cartReqParams = {
      ...cartReqParams,
      attachedLabor
    };
  }

  // Assembly or ProReferral Add on
  if (service?.type) {
    cartReqParams = {
      ...cartReqParams,
      service: {
        type: service.type,
        selected: service.selected
      }
    };
  }

  // subscriptions & Save Add On
  if (subscriptions.frequency) {
    cartReqParams = {
      ...cartReqParams,
      subscriptions
    };
  }

  const pickupService = (fulfillment?.fulfillmentOptions || []).find((option) => option.type === 'pickup');
  const bossFulfillment = (pickupService?.services || []).find((store) => store.type === 'boss');
  const bopisFulfillment = (pickupService?.services || []).find((store) => store.type === 'bopis');
  const nearbyStore = getNearByStore(bopisFulfillment);
  let deliveryZipcode = '';

  if (typeof window !== 'undefined') {
    let zipDetails;
    if (typeof window !== 'undefined' && window?.THD_LOCALIZER_AUTO_INIT?.Localizer?.getDeliveryZipcodeDetails) {
      zipDetails = window?.THD_LOCALIZER_AUTO_INIT?.Localizer?.getDeliveryZipcodeDetails();
    }
    deliveryZipcode = zipDetails?.zipcode;
  }
  let localStore = { locationId: storeId };
  if (bopisFulfillment) {
    localStore = getLocalStore(bopisFulfillment);
  }
  if (bossFulfillment) {
    localStore = getLocalStore(bossFulfillment);
  }
  switch (fulfillmentMethod) {
  case FULFILLMENT_METHOD_STH:
    if (cartReqParams.constructor === Object) {
      cartReqParams.fulfillmentLocation = deliveryZipcode || zipCode;
    } else {
      cartReqParams = cartReqParams.map((cart) => {
        cartReqParams.fulfillmentLocation = deliveryZipcode || zipCode;
        return cart;
      });
    }
    break;
  case FULFILLMENT_METHOD_BOSS:
    if (cartReqParams.constructor === Object) {
      cartReqParams.fulfillmentLocation = localStore.locationId;
    } else {
      cartReqParams = cartReqParams.map((cart) => {
        cartReqParams.fulfillmentLocation = localStore.locationId;
        return cart;
      });
    }
    break;
  case FULFILLMENT_METHOD_BOPIS:
    if (cartReqParams.constructor === Object) {
      cartReqParams.fulfillmentLocation = nearbyStore ? nearbyStore.locationId : localStore.locationId;
    } else {
      cartReqParams = cartReqParams.map((cart) => {
        cartReqParams.fulfillmentLocation = nearbyStore ? nearbyStore.locationId : localStore.locationId;
        return cart;
      });
    }
    break;
  case FULFILLMENT_METHOD_BODFS:
    if (cartReqParams.constructor === Object) {
      cartReqParams.fulfillmentLocation = deliveryZipcode || storeZip;
    } else {
      cartReqParams = cartReqParams.map((cart) => {
        cartReqParams.fulfillmentLocation = deliveryZipcode || storeZip;
        return cart;
      });
    }
    break;
  case FULFILLMENT_METHOD_APPLIANCE:
    if (cartReqParams.constructor === Object) {
      cartReqParams.fulfillmentLocation = zipCode;
      if (primaryStoreNum) {
        cartReqParams.applianceDeliveryStore = primaryStoreNum;
      }
    } else {
      cartReqParams = cartReqParams.map((cart) => {
        cartReqParams.fulfillmentLocation = zipCode;
        if (primaryStoreNum) {
          cartReqParams.applianceDeliveryStore = primaryStoreNum;
        }
        return cart;
      });
    }
    break;
  default:
    break;
  }
  if (isAssembleItemAtStore && fulfillmentMethod !== FULFILLMENT_METHOD_STH) {
    cartReqParams.assembleItemAtStore = STORE_ASSEMBLY_VALUES.SELECTED;
  } else if (isAssembleItemAtStore === false && fulfillmentMethod !== FULFILLMENT_METHOD_STH) {
    cartReqParams.assembleItemAtStore = STORE_ASSEMBLY_VALUES.UN_SELECTED;
  } else {
    delete cartReqParams.assembleItemAtStore;
  }

  if (bogoInfo && bogoInfo.bogoSelectedProduct) {
    const bogoItemReqParams = getBogoCartReqParams(
      bogoInfo.bogoSelectedProduct,
      {
        fulfillmentMethod,
        storeZip,
        zipCode,
        storeId,
        primaryStoreNum
      }
    );
    if (bogoItemReqParams) return [cartReqParams, bogoItemReqParams];
  }

  return cartReqParams;
};

export const useATCLocationDDOStash = ({ stash, itemId }) => {
  const {
    component, pageType, sharedSection, section
  } = stash;

  // stash
  return {
    pageType: pageType || 'pip',
    sharedSection: sharedSection || '',
    section: section || 'buybox',
    component: component || '',
    target: 'atc',
    items: [{
      productID: itemId,
      index: ''
    }]
  };
};

export const renderShippingFulfillment = ({ fulfillment, availabilityType }) => {
  const deliveryService = (fulfillment?.fulfillmentOptions || []).find((option) => option?.type === 'delivery');
  const shippingFulfillment = (deliveryService?.services || []).find((service) => service?.type === 'sth');
  const expressFulfillment = (deliveryService?.services || []).find((service) => service?.type === 'express delivery');
  if (shippingFulfillment) {
    const deliveryStartDate = shippingFulfillment?.deliveryDates?.startDate;
    const deliveryEndDate = shippingFulfillment?.deliveryDates?.endDate;

    const startDate = formatDate(deliveryStartDate);
    const endDate = formatDate(deliveryEndDate);
    const sthTemplate = (
      <>
        {shippingFulfillment.hasFreeShipping && (
          <span className="u__text--success">Free Delivery</span>
        )}
        {!shippingFulfillment.hasFreeShipping
          && shippingFulfillment.freeDeliveryThreshold && (
          <span className="u__text--success">
            {`Free Delivery on $${parseInt(shippingFulfillment.freeDeliveryThreshold, 10)} order`}
          </span>
        )}
        {!shippingFulfillment.hasFreeShipping
          && !shippingFulfillment.freeDeliveryThreshold && (
          <span className="u__text--success">Standard Delivery</span>
        )}
        <span> - </span>
        {startDate && (
          <>
            {startDate === endDate && (
              <>
                <span>Get it by </span>
                <span className="u__bold">
                  {`${formatWeek(deliveryEndDate)}, ${endDate}`}
                </span>
              </>
            )}
            {startDate !== endDate && (
              <>
                <span>Get it </span>
                <span className="u__bold">
                  {startDate}
                </span>
                {endDate && (
                  <span className="u__bold"> - {endDate}</span>
                )}
              </>
            )}
          </>
        )}
      </>
    );
    return {
      method: FULFILLMENT_METHOD_STH,
      showATC: true,
      template: sthTemplate
    };
  }
  if (expressFulfillment) {
    let bodfsTemplate = <span className="u__text--success">Schedule Delivery</span>;
    if (expressFulfillment.deliveryTimeline.toUpperCase() === 'TODAY') {
      bodfsTemplate = (
        <span className="u__text--success">Same-day delivery</span>
      );
    } else if (expressFulfillment.deliveryTimeline.toUpperCase() === 'TOMORROW') {
      bodfsTemplate = (
        <span className="u__text--success">Next-day delivery</span>
      );
    }
    return {
      method: FULFILLMENT_METHOD_BODFS,
      showATC: true,
      template: bodfsTemplate
    };
  }
  if (!fulfillment?.fulfillmentOptions && availabilityType?.type === 'Online') {
    return {
      method: FULFILLMENT_METHOD_STH,
      showATC: false,
      oos: true,
      template: (<span className="u__text--danger">Out of stock online</span>)
    };
  }
  return {
    method: FULFILLMENT_METHOD_STH,
    showATC: false,
    template: (<div> Delivery is <span className="u__text--danger">unavailable</span> for this product </div>)
  };
};

export const getSelectedAddOnInfo = (addOnData = {}, fulfillmentMethod = '') => {
  let addOn = {};
  let flags = {};
  const { checked, addOnType, category } = addOnData;

  switch (addOnType) {
  case HDPP:
    addOn = {
      warrantyItem: {
        itemId: checked ? addOnData.itemId : null
      }
    };
    break;
  case ATTACH_LABOR:
    addOn = {
      attachedLabor: [{
        attachedLaborSku: checked ? addOnData.itemId : null
      }]
    };
    break;
  case ATTACH_LABOR_STORE_ASSEMBLY:
    flags.assembleItemAtStore = fulfillmentMethod === FULFILLMENT_METHOD_BOPIS ? checked : null;
    break;
  case PRO_ASSEMBLY:
    addOn = {
      service: {
        type: checked ? 'Assembly' : null,
        selected: checked,
        category: PRO_ASSEMBLY
      }
    };
    break;
  case PRO_REFERRAL:
    addOn = {
      service: {
        type: checked ? category : null,
        selected: checked,
        category: 'proReferral'
      }
    };
    break;
  case DUAL_PATH:
    addOn = {
      dualPath: checked
    };
    break;
  case SAM_DUAL_PATH:
    addOn = {
      dualPath: checked
    };
    break;
  case VIRTUAL_ASSISTANCE:
    addOn = {
      virtualAssistance: checked
    };
    break;
  default:
    addOn = {};
  }
  return { addOn, flags };
};

const getPickupFulfillment = (fulfillment) => {
  const pickupFulfillment = (fulfillment?.fulfillmentOptions || []).find((option) => option.type === 'pickup');
  return pickupFulfillment;
};

const getDeliveryFulfillment = (fulfillment) => {
  const deliveryFulfillment = (fulfillment?.fulfillmentOptions || []).find((option) => option.type === 'delivery');
  return deliveryFulfillment;
};

const getBopisPrimaryStoreLocation = (fulfillment) => {
  const pickupFulfillment = getPickupFulfillment(fulfillment);
  const bopisFulfillment = (pickupFulfillment?.services || []).find((service) => service.type === 'bopis');
  const bopisPrimaryStoreLocation = (bopisFulfillment?.locations || []).find(
    (location) => location.isAnchor && location?.type === 'store');
  return bopisPrimaryStoreLocation;
};

const getBopisNearbyStoreLocation = (fulfillment) => {
  const pickupFulfillment = getPickupFulfillment(fulfillment);
  const bopisFulfillment = (pickupFulfillment?.services || []).find((service) => service.type === 'bopis');
  const bopisNearbyStoreLocation = (bopisFulfillment?.locations || []).find(
    (location) => !location.isAnchor && location?.type === 'store');
  return bopisNearbyStoreLocation;
};

const getBossLocation = (fulfillment) => {
  const pickupFulfillment = getPickupFulfillment(fulfillment);
  const bossFulfillment = (pickupFulfillment?.services || []).find((service) => service.type === 'boss');
  const bossLocation = (bossFulfillment?.locations || []).find((location) => location?.type === 'online');
  return bossLocation;
};

const getSthLocation = (fulfillment) => {
  const deliveryFulfillment = getDeliveryFulfillment(fulfillment);
  const sthFulfillment = (deliveryFulfillment?.services || []).find((service) => service.type === 'sth');
  const sthLocation = (sthFulfillment?.locations || []).find((location) => location?.type === 'online');
  return sthLocation;
};

const getBodfsLocation = (fulfillment) => {
  const deliveryFulfillment = getDeliveryFulfillment(fulfillment);
  const bodfsFulfillment = (deliveryFulfillment?.services || []).find((service) => service.type === 'express delivery');
  const bodfsLocation = (bodfsFulfillment?.locations || []).find((location) => location?.type === 'store');
  return bodfsLocation;
};

const getQuantityFromLocation = (location) => {
  return location?.inventory?.quantity || 0;
};

export const getFulfillmentMaxQuantity = ({ fulfillment = {}, isTwoTileEnabledFS = false, qty = 1 }) => {
  if (!isTwoTileEnabledFS) { return qty; }

  let maximumInventoryQuantity = 1;
  // extract the inventory quantity for the buyboxState.fulfillmentMethod
  const sthLocation = getSthLocation(fulfillment);
  const sthInventory = getQuantityFromLocation(sthLocation);

  // get the inventory quantity for bodfs
  const bodfsLocation = getBodfsLocation(fulfillment);
  const bodfsInventory = getQuantityFromLocation(bodfsLocation);

  // use the maximum quantity provided for the delivery option
  maximumInventoryQuantity = bodfsInventory > sthInventory ? bodfsInventory : sthInventory;

  const bopisPrimaryStoreLocation = getBopisPrimaryStoreLocation(fulfillment);
  const bopisNearbyStoreLocation = getBopisNearbyStoreLocation(fulfillment);

  const bopisPrimaryStoreInventory = getQuantityFromLocation(bopisPrimaryStoreLocation);
  const bopisNearbyInventory = getQuantityFromLocation(bopisNearbyStoreLocation);
  const bopisInventory = bopisPrimaryStoreInventory > bopisNearbyInventory ? bopisPrimaryStoreInventory
    : bopisNearbyInventory;

  const bossLocation = getBossLocation(fulfillment);
  const bossInventory = getQuantityFromLocation(bossLocation);
  const maxPickupInventory = bopisInventory > bossInventory ? bopisInventory : bossInventory;

  // use the maximum quantity provided for pickup and delivery
  maximumInventoryQuantity = maximumInventoryQuantity > maxPickupInventory ? maximumInventoryQuantity
    : maxPickupInventory;

  return maximumInventoryQuantity;
};